// src/app/RootGate.tsx
// ────────────────────────────────────────────────────────────────────────────
// RootGate — decides what to render at `/`.
//   • Authenticated user → redirect to `/developer` (full app shell)
//   • Unauthenticated    → render PublicHome (anonymous visitor landing)
//
// Sits outside the <RequireAuth> guarded branch so anonymous visitors land
// on the public simulator instead of being redirected to /login. Login
// remains reachable at /login via the header "Sign in" link.
// ────────────────────────────────────────────────────────────────────────────
import React, { Suspense } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth, useRbac } from '@/shared/auth'
import { isVisitorUser } from '@/shared/auth/isVisitorUser'
import { resolveAuthenticatedHome } from '@/shared/auth/shouldForceTableOpsLanding'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const PublicHome = lazyWithRetry(() => import('@/features/public/PublicHome'))

export default function RootGate() {
  const { user, loading, nav, refresh } = useAuth()
  const { capabilities } = useRbac()
  const attemptedRootHydrationRef = React.useRef(false)

  const isAuthenticated = !!(user && !isVisitorUser(user))
  const authenticatedHome = isAuthenticated
    ? resolveAuthenticatedHome({ user, capabilities, nav })
    : '/'

  React.useEffect(() => {
    if (!isAuthenticated) return
    if (loading) return
    if (authenticatedHome !== '/') return
    if (attemptedRootHydrationRef.current) return
    attemptedRootHydrationRef.current = true
    void refresh(true)
  }, [authenticatedHome, isAuthenticated, loading, refresh])

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
        <div className="animate-pulse">Loading…</div>
      </div>
    )
  }

  if (isAuthenticated) {
    if (authenticatedHome === '/' && !attemptedRootHydrationRef.current) {
      return (
        <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
          <div className="animate-pulse">Loading…</div>
        </div>
      )
    }
    const target = authenticatedHome === '/' ? '/tableops' : authenticatedHome
    return <Navigate to={target} replace />
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
          <div className="animate-pulse">Loading…</div>
        </div>
      }
    >
      <PublicHome />
    </Suspense>
  )
}
